import { motion } from "framer-motion";

const companies = [
  "Vardhman",
  "Sutlej Textiles", 
  "Chenab Group",
  "Nahar Spinning Mills",
  "Birla Spinning Mill Baddi"
];

export default function CompanyLogos() {
  return (
    <section className="py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden relative">
      {/* Background decorations */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-br from-orange-500/10 to-orange-600/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-gradient-to-tr from-blue-500/10 to-blue-600/5 rounded-full blur-3xl"></div>
      
      <div className="container relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-block px-4 py-2 bg-gradient-to-r from-orange-500/20 to-orange-600/20 text-orange-300 text-sm font-semibold rounded-full border border-orange-400/30 backdrop-blur-sm mb-6">
            Trusted Partners
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white via-orange-200 to-white bg-clip-text text-transparent">
            Our Valued Partners
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Trusted by leading textile companies across India for high-quality machinery components and exceptional service
          </p>
        </motion.div>

        {/* Moving logos container */}
        <div className="relative">
          <div className="flex overflow-hidden">
            <motion.div
              className="flex items-center gap-12 whitespace-nowrap"
              animate={{
                x: [0, -100 * companies.length]
              }}
              transition={{
                x: {
                  repeat: Infinity,
                  repeatType: "loop",
                  duration: 20,
                  ease: "linear",
                },
              }}
            >
              {/* First set of logos */}
              {companies.map((company, index) => (
                <div
                  key={`first-${index}`}
                  className="flex-shrink-0 px-12 py-8"
                >
                  <motion.div
                    className="relative group"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-orange-600/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="relative bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl px-8 py-6 group-hover:border-orange-400/50 transition-all duration-300">
                      <motion.h3 
                        className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-white via-orange-200 to-white bg-clip-text text-transparent whitespace-nowrap tracking-wide"
                        style={{
                          fontFamily: "'Inter', 'system-ui', sans-serif",
                          textShadow: "0 2px 8px rgba(0,0,0,0.3)"
                        }}
                        whileHover={{
                          backgroundImage: "linear-gradient(45deg, #ffffff, #fb923c, #ea580c, #ffffff)",
                          transition: { duration: 0.4 }
                        }}
                      >
                        {company}
                      </motion.h3>
                    </div>
                  </motion.div>
                </div>
              ))}
              
              {/* Duplicate set for seamless loop */}
              {companies.map((company, index) => (
                <div
                  key={`second-${index}`}
                  className="flex-shrink-0 px-12 py-8"
                >
                  <motion.div
                    className="relative group"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-orange-600/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="relative bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl px-8 py-6 group-hover:border-orange-400/50 transition-all duration-300">
                      <motion.h3 
                        className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-white via-orange-200 to-white bg-clip-text text-transparent whitespace-nowrap tracking-wide"
                        style={{
                          fontFamily: "'Inter', 'system-ui', sans-serif",
                          textShadow: "0 2px 8px rgba(0,0,0,0.3)"
                        }}
                        whileHover={{
                          backgroundImage: "linear-gradient(45deg, #ffffff, #fb923c, #ea580c, #ffffff)",
                          transition: { duration: 0.4 }
                        }}
                      >
                        {company}
                      </motion.h3>
                    </div>
                  </motion.div>
                </div>
              ))}
            </motion.div>
          </div>
          
          {/* Gradient overlays for smooth edges */}
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-slate-900 to-transparent pointer-events-none z-20"></div>
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-slate-900 to-transparent pointer-events-none z-20"></div>
        </div>

        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
            Join our growing network of satisfied partners who trust us with their textile machinery needs
          </p>
          <div className="flex justify-center items-center space-x-8 text-slate-500">
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-400">15+</div>
              <div className="text-sm">Major Partners</div>
            </div>
            <div className="w-px h-12 bg-slate-700"></div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-400">500+</div>
              <div className="text-sm">Projects Delivered</div>
            </div>
            <div className="w-px h-12 bg-slate-700"></div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-400">99%</div>
              <div className="text-sm">Client Retention</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}